import type { PinV1 } from './PinV1'

type BasePinFields = {
  fact_table: string
  kpi: string
  params: PinV1['walk_call']['params']
  // FY v2: REQUIRED here though optional in the schema — TypeScript forces every creation
  // site to record the display unit; only legacy stored pins may lack it.
  render_unit: NonNullable<PinV1['render_hint']['render_unit']>
  viz_params?: Record<string, unknown>
}

/**
 * Discriminated union of valid (walk_function, chart_type) pairs.
 * Adding a new arm requires both fields to match — mismatches are caught at
 * compile time rather than producing a runtime "unsupported entry" error.
 */
export type BuildPinV1Args =
  | ({ walk_function: 'walk_timeline'; chart_type: 'timeline' } & BasePinFields)
  | ({ walk_function: 'walk_absolute'; chart_type: 'absolute_bar' } & BasePinFields)
  | ({ walk_function: 'walk_wrapper'; chart_type: 'wrapper' } & BasePinFields)
  // Ratio kpis (Sales RoI/CpO/OpC/Mediashare). Same chart_types as the standard
  // walks; fact_table is stored for shape compatibility only — the ratio replay
  // arms (ChartFromPin) pass kpi as p_ratio_kpi and never forward fact_table.
  | ({ walk_function: 'walk_ratio'; chart_type: 'timeline' } & BasePinFields)
  | ({ walk_function: 'walk_ratio_absolute'; chart_type: 'absolute_bar' } & BasePinFields)
  // Summary overview strip — always walk_timeline (D1: no ratio strip). Renders the
  // grand-total-per-period overview bar; no value_type filter (sums all rows).
  | ({ walk_function: 'walk_timeline'; chart_type: 'summary_bar_strip' } & BasePinFields)
  // Metric|driver side-by-side bars (WP-2 slice 2). Metric walk = walk_ratio_absolute
  // (RoI/CpO/OpC/Mediashare) or walk_absolute (volumes/nsp); the driver binding + selected
  // timeframes ride in viz_params (re-fired by MetricDriverBarsFromPin).
  | ({ walk_function: 'walk_ratio_absolute'; chart_type: 'metric_driver_bars' } & BasePinFields)
  | ({ walk_function: 'walk_absolute'; chart_type: 'metric_driver_bars' } & BasePinFields)
  // Merged-Dynamics 3-section tile (WP-2 slice 3). Metric timeline walk = walk_ratio (ratios) or
  // walk_timeline (volumes); driver binding + frozen resolved timeframes ride in viz_params
  // (re-fired per slot by DynamicsPanelsFromPin).
  | ({ walk_function: 'walk_ratio'; chart_type: 'dynamics_panels' } & BasePinFields)
  | ({ walk_function: 'walk_timeline'; chart_type: 'dynamics_panels' } & BasePinFields)
  // Multi-timeframe overlay (WP-2 slice 4). N picked timeframes overlaid as N lines; the resolved
  // timeframes ride in viz_params (re-fired by MultiTimeframeOverlayFromPin). Metric walk = walk_ratio
  // (ratios) or walk_timeline (volumes).
  | ({ walk_function: 'walk_timeline'; chart_type: 'multi_timeframe_overlay' } & BasePinFields)
  | ({ walk_function: 'walk_ratio'; chart_type: 'multi_timeframe_overlay' } & BasePinFields)

/**
 * Constructs a PinV1 object from the given arguments.
 *
 * The `walk_function` and `chart_type` fields determine the dispatch key
 * (`${walk_function}:${chart_type}`) used by ChartFromPin. Callers must
 * ensure these match a registered entry in chartDispatch.
 *
 * `params` is typed as PinV1['walk_call']['params'] so callers must supply
 * the required fields (from, to, filter_dims, group_by) at compile time;
 * optional bucket_size/aggregation are passed through.
 */
export function buildPinV1({
  walk_function,
  fact_table,
  kpi,
  params,
  chart_type,
  render_unit,
  viz_params,
}: BuildPinV1Args): PinV1 {
  return {
    schema_version: 1,
    walk_call: {
      function: walk_function,
      fact_table,
      kpi,
      params,
    },
    render_hint: viz_params ? { chart_type, render_unit, viz_params } : { chart_type, render_unit },
  }
}
